import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X, Key, RotateCw, Trash2, Copy, Check } from "lucide-react";
import { ApiIntegrationType } from "@finora/types";
import type { CloudApiIntegrationDto } from "@finora/types";
import { ApiError } from "@finora/api-client";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { KeyReveal } from "../CompanySettings/KeyReveal";

const TYPE_LABELS: Record<ApiIntegrationType, string> = {
  WOOCOMMERCE: "WooCommerce",
  WHMCS: "WHMCS",
  MAGENTO: "Magento",
  SHOPIFY: "Shopify",
  POS: "POS / kasa",
  ERP: "ERP",
  CUSTOM: "Ostalo",
  BANK_IMPORT: "Uvoz bankovnih izvoda (email)",
};

function NewIntegrationModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [type, setType] = useState<ApiIntegrationType>(ApiIntegrationType.CUSTOM);
  const [businessLocationId, setBusinessLocationId] = useState("");

  const { data: locations } = useQuery({
    queryKey: ["business-location-options"],
    queryFn: () => api.listBusinessLocationOptions(),
  });

  const save = useMutation({
    mutationFn: () =>
      api.createCloudApiIntegration({ name, type, businessLocationId: businessLocationId || null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cloud-api-integrations"] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Nova integracija</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Naziv</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="npr. WooCommerce - Prodavnica ABC"
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Tip</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as ApiIntegrationType)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            >
              {Object.values(ApiIntegrationType).map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Poslovna lokacija (opciono)
            </label>
            <select
              value={businessLocationId}
              onChange={(e) => setBusinessLocationId(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            >
              <option value="">Bilo koja - koristi podrazumevanu automatski</option>
              {locations?.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                  {loc.isDefault ? " (podrazumevana)" : ""}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-400">
              Ako firma ima više prodajnih mesta, vezivanje integracije za jednu lokaciju znači da
              /v1/fiscalize ne mora da je navodi u svakom zahtevu.
            </p>
          </div>
        </div>
        <div className="flex gap-3 border-t border-border px-5 py-4">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            Otkaži
          </Button>
          <Button className="flex-1" disabled={!name || save.isPending} onClick={() => save.mutate()}>
            Sačuvaj
          </Button>
        </div>
      </div>
    </div>
  );
}

function NewKeyRevealModal({
  rawKey,
  onClose,
}: {
  rawKey: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-surface shadow-xl">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Novi API ključ</h2>
        </div>
        <div className="space-y-3 px-5 py-5">
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Ovo je jedini put da vidite pun ključ - sačuvajte ga sada, posle se ne može ponovo prikazati.
          </p>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-app-bg px-3 py-2 font-mono text-sm break-all">
            {rawKey}
          </div>
          <button
            type="button"
            onClick={async () => {
              await navigator.clipboard.writeText(rawKey).catch(() => {
                const el = document.createElement("textarea");
                el.value = rawKey;
                document.body.appendChild(el);
                el.select();
                document.execCommand("copy");
                document.body.removeChild(el);
              });
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className="flex items-center gap-1.5 text-sm text-brand hover:underline"
          >
            {copied ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
            {copied ? "Kopirano" : "Kopiraj ključ"}
          </button>
        </div>
        <div className="flex justify-end border-t border-border px-5 py-4">
          <Button onClick={onClose}>Zatvorio sam ovo negde bezbedno</Button>
        </div>
      </div>
    </div>
  );
}

function IntegrationCard({ integration }: { integration: CloudApiIntegrationDto }) {
  const queryClient = useQueryClient();
  const [revealKey, setRevealKey] = useState<string | null>(null);
  const [editingLocation, setEditingLocation] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["cloud-api-integrations"] });

  const { data: locations } = useQuery({
    queryKey: ["business-location-options"],
    queryFn: () => api.listBusinessLocationOptions(),
    enabled: editingLocation,
  });

  const updateLocation = useMutation({
    mutationFn: (businessLocationId: string | null) =>
      api.updateCloudApiIntegrationLocation(integration.id, businessLocationId),
    onSuccess: () => {
      invalidate();
      setEditingLocation(false);
    },
  });

  const toggleActive = useMutation({
    mutationFn: () =>
      integration.status === "ACTIVE"
        ? api.disableCloudApiIntegration(integration.id)
        : api.enableCloudApiIntegration(integration.id),
    onSuccess: invalidate,
  });

  const createKey = useMutation({
    mutationFn: () => api.createCloudApiKey(integration.id),
    onSuccess: (key) => {
      setRevealKey(key.rawKey);
      invalidate();
    },
  });

  const rotateKey = useMutation({
    mutationFn: (apiKeyId: string) => api.rotateCloudApiKey(integration.id, apiKeyId),
    onSuccess: (key) => {
      setRevealKey(key.rawKey);
      invalidate();
    },
  });

  const revokeKey = useMutation({
    mutationFn: (apiKeyId: string) => api.revokeCloudApiKey(integration.id, apiKeyId),
    onSuccess: invalidate,
  });

  const removeIntegration = useMutation({
    mutationFn: () => api.deleteCloudApiIntegration(integration.id),
    onSuccess: invalidate,
  });

  const deleteKey = useMutation({
    mutationFn: (apiKeyId: string) => api.deleteCloudApiKey(integration.id, apiKeyId),
    onSuccess: invalidate,
  });

  return (
    <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-slate-800">{integration.name}</p>
          <p className="text-xs text-slate-400">{TYPE_LABELS[integration.type]}</p>
          {!editingLocation ? (
            <button
              onClick={() => setEditingLocation(true)}
              className="mt-0.5 text-left text-xs text-slate-500 hover:text-brand hover:underline"
            >
              {integration.businessLocationName ? (
                <>Poslovna lokacija: {integration.businessLocationName}</>
              ) : (
                <span className="text-amber-600">Poslovna lokacija nije izabrana</span>
              )}
            </button>
          ) : (
            <div className="mt-1 flex items-center gap-2">
              <select
                defaultValue={integration.businessLocationId ?? ""}
                onChange={(e) => updateLocation.mutate(e.target.value || null)}
                disabled={updateLocation.isPending}
                className="h-8 rounded-lg border border-border px-2 text-xs focus:border-brand focus:outline-none"
              >
                <option value="">Nije vezana (automatski / po zahtevu)</option>
                {locations?.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                    {loc.isDefault ? " (podrazumevana)" : ""}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setEditingLocation(false)}
                className="text-xs text-slate-400 hover:text-slate-600"
              >
                Otkaži
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {integration.status === "ACTIVE" ? (
            <span className="text-xs text-emerald-600">Aktivna</span>
          ) : (
            <span className="text-xs text-red-500">Deaktivirana</span>
          )}
          <Button
            size="sm"
            variant={integration.status === "ACTIVE" ? "danger" : "secondary"}
            disabled={toggleActive.isPending}
            onClick={() => toggleActive.mutate()}
          >
            {integration.status === "ACTIVE" ? "Deaktiviraj" : "Aktiviraj"}
          </Button>
          {integration.apiKeys.length === 0 && (
            <Button
              size="sm"
              variant="danger"
              disabled={removeIntegration.isPending}
              onClick={() => {
                if (window.confirm(`Obrisati integraciju „${integration.name}“?`)) removeIntegration.mutate();
              }}
            >
              Obriši
            </Button>
          )}
        </div>
      </div>

      <div className="mt-3 border-t border-border pt-3">
        <div className="mb-2 flex items-center justify-between">
          <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
            <Key className="h-3.5 w-3.5" aria-hidden /> API ključevi
          </p>
          <button
            onClick={() => createKey.mutate()}
            disabled={createKey.isPending}
            className="flex items-center gap-1 text-xs text-brand hover:underline"
          >
            <Plus className="h-3 w-3" aria-hidden /> Novi ključ
          </button>
        </div>
        {/* Firma sme imati najviše 1 aktivan ključ ukupno (preko svih
            integracija) - vidi razgovor "API integracija max 1 kljuc". */}
        {createKey.isError && (
          <p className="mb-2 rounded-lg bg-amber-50 px-2 py-1.5 text-xs text-amber-700">
            {createKey.error instanceof ApiError
              ? createKey.error.response.message
              : "Kreiranje ključa nije uspelo."}
          </p>
        )}
        {removeIntegration.isError && (
          <p className="mb-2 rounded-lg bg-amber-50 px-2 py-1.5 text-xs text-amber-700">
            {removeIntegration.error instanceof ApiError
              ? removeIntegration.error.response.message
              : "Brisanje nije uspelo."}
          </p>
        )}
        {integration.apiKeys.length === 0 ? (
          <p className="text-xs text-slate-400">Još nema izdatih ključeva.</p>
        ) : (
          <div className="space-y-1.5">
            {integration.apiKeys.map((key) => (
              <div
                key={key.id}
                className="flex items-center justify-between rounded-lg bg-app-bg px-3 py-1.5 text-xs"
              >
                <span className="font-mono text-slate-600">
                  {key.keyPrefix}…{key.revokedAt && <span className="ml-2 text-red-500">opozvan</span>}
                  {!key.revokedAt && <KeyReveal value={key.rawKey} />}
                </span>
                {!key.revokedAt ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => rotateKey.mutate(key.id)}
                      disabled={rotateKey.isPending}
                      className="flex items-center gap-1 text-slate-500 hover:text-brand"
                      title="Rotiraj"
                    >
                      <RotateCw className="h-3.5 w-3.5" aria-hidden />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm("Opozvati ovaj ključ? Integracija koja ga koristi prestaje da radi.")) {
                          revokeKey.mutate(key.id);
                        }
                      }}
                      disabled={revokeKey.isPending}
                      className="flex items-center gap-1 text-slate-500 hover:text-red-600"
                      title="Opozovi"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      if (window.confirm("Trajno obrisati ovaj opozvan ključ sa liste?")) {
                        deleteKey.mutate(key.id);
                      }
                    }}
                    disabled={deleteKey.isPending}
                    className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-600"
                    title="Trajno obriši"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden /> Obriši
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {revealKey && <NewKeyRevealModal rawKey={revealKey} onClose={() => setRevealKey(null)} />}
    </div>
  );
}

export function IntegrationsTab() {
  const [modalOpen, setModalOpen] = useState(false);
  const { data: integrations, isLoading } = useQuery({
    queryKey: ["cloud-api-integrations"],
    queryFn: () => api.listCloudApiIntegrations(),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setModalOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Nova integracija
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : !integrations || integrations.length === 0 ? (
        <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
          <EmptyState
            icon={Key}
            title="Još nema integracija"
            description='Dodajte prvu integraciju (npr. WooCommerce, POS) dugmetom "Nova integracija".'
          />
        </div>
      ) : (
        <div className="space-y-3">
          {integrations.map((integration) => (
            <IntegrationCard key={integration.id} integration={integration} />
          ))}
        </div>
      )}

      {modalOpen && <NewIntegrationModal onClose={() => setModalOpen(false)} />}
    </div>
  );
}
